import React, { useState, useEffect } from 'react';
import { MapPin, Phone, User, ArrowRight, Zap, TrendingUp, Leaf, DollarSign, X, Check, Share2, MessageCircle, Info, CreditCard, ChevronLeft, Download, Calculator, Battery, Sun, PlugZap, ArrowLeftRight, Home, Building2 } from 'lucide-react';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import axios from 'axios';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import FloatingButtons from '../components/FloatingButtons';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Imágenes de sistemas según cantidad de paneles
const systemImages = {
  small: 'https://images.unsplash.com/photo-1509391366360-2e959784a276?w=1200&q=80',
  medium: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=1200&q=80',
  large: 'https://images.unsplash.com/photo-1497440001374-f26997328c1b?w=1200&q=80',
  xlarge: 'https://images.unsplash.com/photo-1473341304170-971dccb5ac1e?w=1200&q=80',
  xxlarge: 'https://images.unsplash.com/photo-1508514177221-188b1cf16e9d?w=1200&q=80'
};

const CalculadoraPage = () => {
  const [step, setStep] = useState('form');
  const [showPopup, setShowPopup] = useState(false);
  const [popupType, setPopupType] = useState('financing');
  const [formData, setFormData] = useState({
    // Nuevo: Consumo en kWh en lugar de monto de factura
    monthlyKwh: 300, // Consumo mensual en kWh
    coveragePercent: 100, // Porcentaje de cobertura deseado (10-100%)
    address: '',
    name: '',
    whatsapp: '+595',
    useGeolocation: false,
    propertyType: 'residencial',
    tarifaType: 'residencial', // 'residencial' o 'industria'
    systemType: 'ongrid' // Default to On-Grid for new calculator
  });
  const [result, setResult] = useState(null);
  const [budgetId, setBudgetId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [geoloading, setGeoloading] = useState(false);
  const [installments, setInstallments] = useState(12);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isSharedBudget, setIsSharedBudget] = useState(false);
  const [settings, setSettings] = useState(null);
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [calculatorConfig, setCalculatorConfig] = useState(null);
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [showManualBillInput, setShowManualBillInput] = useState(false);
  const [manualBillValue, setManualBillValue] = useState('');
  const addressInputRef = React.useRef(null);

  // Ciudades y barrios principales de Paraguay para autocompletado
  const paraguayLocations = [
    // Asunción y Gran Asunción
    'Asunción, Paraguay',
    'Asunción Centro, Asunción',
    'Villa Morra, Asunción',
    'Carmelitas, Asunción',
    'Recoleta, Asunción',
    'Sajonia, Asunción',
    'Trinidad, Asunción',
    'Lambaré, Central',
    'San Lorenzo, Central',
    'Fernando de la Mora, Central',
    'Luque, Central',
    'Mariano Roque Alonso, Central',
    'Capiatá, Central',
    'Ñemby, Central',
    'San Antonio, Central',
    'Villa Elisa, Central',
    'Limpio, Central',
    'Itauguá, Central',
    'Areguá, Central',
    'Ypacaraí, Central',
    // Ciudades del Interior
    'Ciudad del Este, Alto Paraná',
    'Encarnación, Itapúa',
    'Pedro Juan Caballero, Amambay',
    'Coronel Oviedo, Caaguazú',
    'Caaguazú, Caaguazú',
    'Villarrica, Guairá',
    'Concepción, Concepción',
    'Pilar, Ñeembucú',
    'San Estanislao, San Pedro',
    'Caacupé, Cordillera',
    'Paraguarí, Paraguarí',
    'Salto del Guairá, Canindeyú',
    'Hernandarias, Alto Paraná',
    'Presidente Franco, Alto Paraná',
    'Minga Guazú, Alto Paraná',
    'Santa Rita, Alto Paraná',
    'Filadelfia, Boquerón',
    'Loma Plata, Boquerón',
    'Neuland, Boquerón',
    'Mariscal Estigarribia, Boquerón'
  ];

  // Load settings from API
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const [settingsRes, configRes] = await Promise.all([
          axios.get(`${API}/settings/public`),
          axios.get(`${API}/products/public/calculator-config`)
        ]);
        setSettings(settingsRes.data);
        setCalculatorConfig(configRes.data);
      } catch (error) {
        console.error('Error loading settings:', error);
        // Use defaults if API fails
        setSettings({
          dollar_rate: 7500,
          ande_kwh_rate_residential: 680,
          ande_kwh_rate_commercial: 750,
          ande_kwh_rate_industrial: 650,
          cost_per_watt_residential: 8500,
          solar_hours_per_day: 5,
          solar_panel_default_watts: 550,
          solar_efficiency_factor: 0.85,
          financing_max_months: 24,
          financing_min_months: 6,
          cash_discount_percent: 5,
          carbon_offset_per_kw: 1200,
          trees_equivalent_per_kw: 40,
          water_saved_liters_per_kw: 1500,
          panel_warranty_years: 25,
          inverter_warranty_years: 10,
          whatsapp_number: '+595991344956'
        });
        setCalculatorConfig({
          panels: [],
          default_values: {
            panel_power_watts: 550,
            daily_sun_hours: 5,
            autoconsumo_factor: 0.60,
            independencia_factor: 1.40
          }
        });
      } finally {
        setSettingsLoading(false);
      }
    };
    loadSettings();
  }, []);

  // Load shared budget from URL
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const budgetParam = urlParams.get('budget');
    
    if (budgetParam) {
      try {
        const decodedData = JSON.parse(decodeURIComponent(atob(budgetParam)));
        
        // Reconstruct the result object from shared data
        const sharedResult = {
          system_capacity_kw: parseFloat(decodedData.system?.replace(' kW', '')) || 0,
          recommended_panels: decodedData.panels || 0,
          installation_cost: parseInt(decodedData.cost?.replace(/[^\d]/g, '')) || 0,
          monthly_savings: parseInt(decodedData.monthlySavings?.replace(/[^\d]/g, '')) || 0,
          annual_savings: parseInt(decodedData.annualSavings?.replace(/[^\d]/g, '')) || 0,
          roi_years: decodedData.roi || 0,
          carbon_offset_kg: (decodedData.panels || 10) * 500, // Estimate based on panels
          customerData: {
            name: decodedData.name || 'Cliente',
            address: 'Dirección no disponible',
            monthlyBill: 0
          }
        };
        
        setBudgetId(decodedData.budgetId);
        setResult(sharedResult);
        setStep('results');
        setIsSharedBudget(true);
      } catch (error) {
        console.error('Error loading shared budget:', error);
      }
    }
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (step === 'results' && !budgetId && !isSharedBudget) {
      const randomNum = Math.floor(Math.random() * 999999).toString().padStart(6, '0');
      setBudgetId(randomNum);
    }
    // Scroll to top when showing results
    if (step === 'results') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [step, budgetId, isSharedBudget]);

  // Función de autocompletado local para direcciones de Paraguay
  const handleAddressChange = (value) => {
    setFormData(prev => ({ ...prev, address: value, useGeolocation: false }));
    
    if (value.length >= 2) {
      const filtered = paraguayLocations.filter(location =>
        location.toLowerCase().includes(value.toLowerCase())
      );
      setAddressSuggestions(filtered.slice(0, 6)); // Mostrar máximo 6 sugerencias
      setShowSuggestions(filtered.length > 0);
    } else {
      setAddressSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const selectAddressSuggestion = (suggestion) => {
    setFormData(prev => ({ ...prev, address: suggestion, useGeolocation: false }));
    setShowSuggestions(false);
    setAddressSuggestions([]);
  };

  const isFormValid = () => {
    return formData.monthlyKwh > 0 && formData.address && formData.name && formData.whatsapp && formData.whatsapp.length > 4;
  };

  // Get color based on kWh consumption level
  const getConsumptionColor = (kwh) => {
    if (kwh <= 150) return 'text-green-500';
    if (kwh <= 500) return 'text-orange-400';
    return 'text-red-400';
  };

  // Calculate bill from kWh using ANDE tariffs (Pliego N°21 - Cat. 142)
  const calculateBillFromKwh = (kwh, tarifaType = 'residencial') => {
    // Tarifa Industrial - precio único
    if (tarifaType === 'industria') {
      return kwh * 404.97;
    }
    
    // Tarifas ANDE - Pliego N°21 - Categoría 142 Residencial
    // El precio se aplica al consumo TOTAL según el nivel alcanzado (no escalonado)
    if (kwh <= 50) return kwh * 311.55;
    if (kwh <= 150) return kwh * 349.89;
    if (kwh <= 300) return kwh * 365.45;
    if (kwh <= 500) return kwh * 403.82;
    if (kwh <= 1000) return kwh * 420.27;
    return kwh * 435.51; // Mayor a 1000 kWh
  };

  // Get tariff rate for a given kWh level
  const getTariffRate = (kwh, tarifaType = 'residencial') => {
    // Tarifa Industrial - precio único
    if (tarifaType === 'industria') {
      return { rate: 404.97, level: 'Tarifa Industrial' };
    }
    
    // Tarifa Residencial escalonada
    if (kwh <= 50) return { rate: 311.55, level: '0-50 kWh' };
    if (kwh <= 150) return { rate: 349.89, level: '51-150 kWh' };
    if (kwh <= 300) return { rate: 365.45, level: '151-300 kWh' };
    if (kwh <= 500) return { rate: 403.82, level: '301-500 kWh' };
    if (kwh <= 1000) return { rate: 420.27, level: '501-1000 kWh' };
    return { rate: 435.51, level: 'Mayor a 1000 kWh' };
  };

  const getGeolocation = () => {
    setGeoloading(true);
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          try {
            const response = await fetch(
              `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`
            );
            const data = await response.json();
            setFormData({ ...formData, address: data.display_name, useGeolocation: true });
          } catch (error) {
            console.error('Error al obtener dirección:', error);
            setFormData({ ...formData, address: `Lat: ${latitude}, Lng: ${longitude}`, useGeolocation: true });
          } finally {
            setGeoloading(false);
          }
        },
        (error) => {
          console.error('Error de geolocalización:', error);
          alert('No se pudo obtener tu ubicación. Por favor ingresa tu dirección manualmente.');
          setGeoloading(false);
        }
      );
    } else {
      alert('Tu navegador no soporta geolocalización.');
      setGeoloading(false);
    }
  };

  // ==================== FUNCIÓN PARA CALCULAR kWh A PARTIR DE FACTURA ====================
  // Pliego de Tarifas Nro 21 - ANDE (Nov 2024) - Categoría 142 Residencial
  // IMPORTANTE: El precio se define por el NIVEL FINAL alcanzado, no por tramos escalonados
  // Si consumís 400 kWh, los 400 kWh se multiplican por el precio de ese nivel (301-500)
  const calculateKwhFromBill = (bill, tiers) => {
    // Tarifas por NIVEL TOTAL (todo el consumo se multiplica por el precio del nivel)
    const level1_rate = tiers["0_50"] || 311.55;      // 0-50 kWh
    const level2_rate = tiers["51_150"] || 349.89;    // 51-150 kWh
    const level3_rate = tiers["151_300"] || 365.45;   // 151-300 kWh
    const level4_rate = tiers["301_500"] || 403.82;   // 301-500 kWh
    const level5_rate = tiers["501_1000"] || 420.27;  // 501-1000 kWh
    const level6_rate = tiers["over_1000"] || 435.51; // >1000 kWh
    
    // Calcular límites de factura para cada nivel (precio × límite superior del nivel)
    const level1_max_bill = 50 * level1_rate;      // 15,577.50 Gs
    const level2_max_bill = 150 * level2_rate;    // 52,483.50 Gs
    const level3_max_bill = 300 * level3_rate;    // 109,635 Gs
    const level4_max_bill = 500 * level4_rate;    // 201,910 Gs
    const level5_max_bill = 1000 * level5_rate;   // 420,270 Gs
    
    let kWh = 0;
    let rate = level1_rate;
    let levelName = '0-50 kWh';
    let levelRange = '0-50';
    
    // Determinar el nivel según el monto de la factura
    if (bill <= level1_max_bill) {
      kWh = bill / level1_rate;
      rate = level1_rate;
      levelName = '0-50 kWh';
      levelRange = '0-50';
    } else if (bill <= level2_max_bill) {
      kWh = bill / level2_rate;
      rate = level2_rate;
      levelName = '51-150 kWh';
      levelRange = '51-150';
    } else if (bill <= level3_max_bill) {
      kWh = bill / level3_rate;
      rate = level3_rate;
      levelName = '151-300 kWh';
      levelRange = '151-300';
    } else if (bill <= level4_max_bill) {
      kWh = bill / level4_rate;
      rate = level4_rate;
      levelName = '301-500 kWh';
      levelRange = '301-500';
    } else if (bill <= level5_max_bill) {
      kWh = bill / level5_rate;
      rate = level5_rate;
      levelName = '501-1000 kWh';
      levelRange = '501-1000';
    } else {
      kWh = bill / level6_rate;
      rate = level6_rate;
      levelName = 'Mayor a 1000 kWh';
      levelRange = '>1000';
    }
    
    return {
      kWh: Math.max(1, Math.round(kWh)),
      rate: rate,
      levelName: levelName,
      levelRange: levelRange
    };
  };

  const handleCalculate = async () => {
    setLoading(true);
    try {
      // ==================== NUEVA LÓGICA: TRINA SOLAR 740W ====================
      // Consumo directo en kWh del usuario
      const monthlyKwh = formData.monthlyKwh;
      const coveragePercent = formData.coveragePercent;
      
      // Use settings from API for calculations
      const s = settings;
      const propertyType = formData.propertyType;
      const systemType = formData.systemType;
      
      // PASO A: Objetivo de energía a generar
      const energyGoalKwh = monthlyKwh * (coveragePercent / 100);
      
      // PASO B: Panel Trina Solar 740W genera 88.8 kWh/mes en Paraguay
      const TRINA_PANEL_WATTS = 740; // Watts
      const TRINA_PANEL_KWH_MONTH = 88.8; // kWh generados por mes por panel
      const TRINA_EFFICIENCY = 23.8; // % Eficiencia
      
      // PASO C: Cantidad de paneles (redondear hacia arriba)
      const recommendedPanels = Math.ceil(energyGoalKwh / TRINA_PANEL_KWH_MONTH);
      
      // Generación real del sistema
      const actualGenerationKwhMonth = recommendedPanels * TRINA_PANEL_KWH_MONTH;
      
      // ==================== CÁLCULO TARIFARIO ANDE - PLIEGO N°21 ====================
      // Regla: "El precio se aplica al consumo TOTAL según el nivel alcanzado (no escalonado)"
      // Para industria: tarifa única de 404,97 Gs/kWh
      const tarifaType = formData.tarifaType || 'residencial';
      const currentTariff = getTariffRate(monthlyKwh, tarifaType);
      const currentBill = calculateBillFromKwh(monthlyKwh, tarifaType);
      
      // Nueva factura: Consumo menos generación solar
      const remainingKwh = Math.max(0, monthlyKwh - actualGenerationKwhMonth);
      const newTariff = getTariffRate(remainingKwh, tarifaType);
      const newBill = calculateBillFromKwh(remainingKwh, tarifaType);
      
      // Ahorro mensual
      const monthlySavings = currentBill - newBill;
      const annualSavings = monthlySavings * 12;
      
      // ==================== CÁLCULO DE COSTOS CON MÁRGENES ====================
      const actualCapacityKw = (recommendedPanels * TRINA_PANEL_WATTS) / 1000;
      
      // Precios de COSTO de equipos (según Proforma Invoice)
      const panelCostUsd = s.panel_price_usd || 85; // $85 USD por panel
      const inverterCostPerKwUsd = s.inverter_price_per_kw_usd || 510; // $510 USD por kW
      const structureCostPerPanelUsd = s.structure_price_per_panel_usd || 47; // $47 USD por panel
      const laborCostPerKwUsd = s.installation_labor_per_kw_usd || 100; // $100 USD por kW
      const batteryCostPerKwhUsd = s.battery_price_per_kwh_usd || 260; // $260 USD por kWh
      
      // Costos base en USD
      const panelsCostUsd = recommendedPanels * panelCostUsd;
      const inverterCostUsd = actualCapacityKw * inverterCostPerKwUsd;
      const structureCostUsd = recommendedPanels * structureCostPerPanelUsd;
      const laborCostUsd = actualCapacityKw * laborCostPerKwUsd;
      const subtotalUsd = panelsCostUsd + inverterCostUsd + structureCostUsd + laborCostUsd;
      const cablingCostUsd = subtotalUsd * ((s.cabling_accessories_percent || 8) / 100);
      
      // Costos adicionales según tipo de sistema
      let batteryCostUsd = 0;
      let batteryCapacityKwh = 0;
      let bidirectionalMeterCostUsd = 0;
      const dailyKwh = monthlyKwh / 30;
      
      if (systemType === 'independiente') {
        batteryCapacityKwh = Math.ceil(dailyKwh * 0.5);
        batteryCostUsd = batteryCapacityKwh * batteryCostPerKwhUsd;
      } else if (systemType === 'ongrid') {
        bidirectionalMeterCostUsd = 400; // Medidor bidireccional
      }
      
      // Costo total base en USD
      const baseCostUsd = subtotalUsd + cablingCostUsd + batteryCostUsd + bidirectionalMeterCostUsd;
      
      // ==================== APLICAR MÁRGENES DE GANANCIA ====================
      // Margen de contingencia (5%)
      const contingencyPercent = s.margin_contingency_percent || 5;
      const withContingency = baseCostUsd * (1 + contingencyPercent / 100);
      
      // Margen bruto de utilidad (25% sobre costos directos)
      const grossMarginPercent = s.margin_gross_profit_percent || 25;
      const finalPriceUsd = withContingency * (1 + grossMarginPercent / 100);
      
      // Convertir a Guaraníes
      const dollarRate = s.dollar_rate || 7500;
      const installationCost = Math.round(finalPriceUsd * dollarRate);
      
      // Convertir costos individuales a Gs para desglose
      const panelsCost = Math.round(panelsCostUsd * dollarRate);
      const inverterCost = Math.round(inverterCostUsd * dollarRate);
      const structureCost = Math.round(structureCostUsd * dollarRate);
      const laborCost = Math.round(laborCostUsd * dollarRate);
      const cablingCost = Math.round(cablingCostUsd * dollarRate);
      const batteryCost = Math.round(batteryCostUsd * dollarRate);
      const bidirectionalMeterCost = Math.round(bidirectionalMeterCostUsd * dollarRate);
      
      // Aplicar descuentos sobre precio final
      let finalCost = installationCost;
      let volumeDiscountApplied = false;
      if (actualCapacityKw >= (s.volume_discount_threshold_kw || 10)) {
        finalCost = Math.round(installationCost * (1 - (s.volume_discount_percent || 3) / 100));
        volumeDiscountApplied = true;
      }
      
      const cashPrice = Math.round(finalCost * (1 - (s.cash_discount_percent || 5) / 100));
      
      // ==================== NUEVO: CÁLCULO AVANZADO DE ROI ====================
      // Fórmula: ROI (%) = ((Total Savings over lifetime - Total System Cost) / Total System Cost) × 100
      
      // Parámetros de configuración
      const systemLifetimeYears = s.system_lifetime_years || 25;
      const annualDegradationRate = (s.annual_degradation_rate || 0.5) / 100; // Convertir a decimal
      const annualMaintenanceCostUsd = s.annual_maintenance_cost_usd || 50; // USD por kW
      const governmentIncentivePercent = (s.government_incentive_percent || 0) / 100;
      const taxCreditPercent = (s.tax_credit_percent || 0) / 100;
      const energyPriceIncreaseRate = (s.energy_price_increase_year || 8) / 100;
      
      // Calcular ahorro total durante la vida útil (considerando degradación y aumento de tarifas)
      let totalSavingsLifetime = 0;
      let currentAnnualSavings = annualSavings;
      let efficiencyFactor = 1; // Empieza al 100%
      
      for (let year = 1; year <= systemLifetimeYears; year++) {
        // Aplicar degradación al ahorro (menos generación = menos ahorro)
        efficiencyFactor = 1 - (annualDegradationRate * (year - 1));
        
        // Aplicar aumento de tarifas eléctricas (más ahorro si las tarifas suben)
        const tariffMultiplier = Math.pow(1 + energyPriceIncreaseRate, year - 1);
        
        // Ahorro del año = Ahorro base × Factor de eficiencia × Multiplicador de tarifa
        const yearSavings = annualSavings * efficiencyFactor * tariffMultiplier;
        totalSavingsLifetime += yearSavings;
      }
      
      // Calcular costo total del sistema (incluyendo mantenimiento durante vida útil - incentivos)
      const totalMaintenanceCostUsd = annualMaintenanceCostUsd * actualCapacityKw * systemLifetimeYears;
      const totalMaintenanceCostGs = totalMaintenanceCostUsd * dollarRate;
      
      // Incentivos y créditos fiscales aplicados al costo inicial
      const incentivesAmount = finalCost * governmentIncentivePercent;
      const taxCreditAmount = finalCost * taxCreditPercent;
      const totalIncentives = incentivesAmount + taxCreditAmount;
      
      // Costo neto del sistema = Instalación + Mantenimiento - Incentivos
      const netSystemCost = finalCost + totalMaintenanceCostGs - totalIncentives;
      
      // ROI Porcentaje = ((Ahorro Total - Costo Total) / Costo Total) × 100
      const roiPercent = netSystemCost > 0 
        ? ((totalSavingsLifetime - netSystemCost) / netSystemCost) * 100 
        : 0;
      
      // Payback Period (años para recuperar inversión)
      let paybackYears = 99;
      let cumulativeSavings = 0;
      let yearCount = 0;
      efficiencyFactor = 1;
      
      while (cumulativeSavings < netSystemCost && yearCount < systemLifetimeYears) {
        yearCount++;
        efficiencyFactor = 1 - (annualDegradationRate * (yearCount - 1));
        const tariffMultiplier = Math.pow(1 + energyPriceIncreaseRate, yearCount - 1);
        const yearSavings = annualSavings * efficiencyFactor * tariffMultiplier;
        cumulativeSavings += yearSavings;
      }
      
      if (cumulativeSavings >= netSystemCost) {
        // Calcular fracción del último año
        const previousCumulative = cumulativeSavings - (annualSavings * efficiencyFactor * Math.pow(1 + energyPriceIncreaseRate, yearCount - 1));
        const remainingToPayback = netSystemCost - previousCumulative;
        const lastYearSavings = annualSavings * efficiencyFactor * Math.pow(1 + energyPriceIncreaseRate, yearCount - 1);
        paybackYears = (yearCount - 1) + (remainingToPayback / lastYearSavings);
      }
      
      // Proyección a vida útil
      const projection25Years = totalSavingsLifetime;
      
      // Impacto ambiental
      const carbonOffsetKg = actualCapacityKw * (s.carbon_offset_per_kw || 1200);
      const treesEquivalent = actualCapacityKw * (s.trees_equivalent_per_kw || 40);
      const waterSavedLiters = actualCapacityKw * (s.water_saved_liters_per_kw || 1500);
      
      // Descripción del sistema según tipo
      let systemDescription, systemRecommendation;
      if (systemType === 'ongrid') {
        systemDescription = 'On-Grid (Inyección a Red)';
        systemRecommendation = `Para tu dirección en ${formData.address}, te recomendamos instalar ${recommendedPanels} paneles solares de alta eficiencia. Con este sistema On-Grid, inyectarás excedentes a ANDE durante el día y usarás los créditos de noche para lograr una factura mínima o cero.`;
      } else if (systemType === 'autoconsumo') {
        systemDescription = 'Autoconsumo Parcial';
        systemRecommendation = `Para tu dirección en ${formData.address}, te recomendamos instalar ${recommendedPanels} paneles solares de alta eficiencia. Sistema de autoconsumo diurno sin baterías, ideal para reducir tu factura.`;
      } else {
        systemDescription = 'Independencia Total';
        systemRecommendation = `Para tu dirección en ${formData.address}, te recomendamos instalar ${recommendedPanels} paneles solares de alta eficiencia con ${batteryCapacityKwh} kWh de baterías para independencia total de la red.`;
      }
      
      // Datos específicos On-Grid
      let onGridData = null;
      if (systemType === 'ongrid') {
        const consumoDiurno = dailyKwh * 0.40;
        const consumoNocturno = dailyKwh * 0.60;
        const dailyGeneration = actualGenerationKwhMonth / 30;
        const excedenteDiario = Math.max(0, dailyGeneration - consumoDiurno);
        const netMeteringEfficiency = 0.95;
        const creditoMensual = excedenteDiario * 30 * currentTariff.rate * netMeteringEfficiency;
        const consumoNocturnoMensualGs = consumoNocturno * 30 * currentTariff.rate;
        
        onGridData = {
          consumo_diurno_kwh: Math.round(consumoDiurno * 10) / 10,
          consumo_nocturno_kwh: Math.round(consumoNocturno * 10) / 10,
          generacion_diaria_kwh: Math.round(dailyGeneration * 10) / 10,
          excedente_diario_kwh: Math.round(excedenteDiario * 10) / 10,
          credito_mensual_gs: Math.round(creditoMensual),
          consumo_nocturno_mensual_gs: Math.round(consumoNocturnoMensualGs),
          balance_mensual: Math.round(creditoMensual - consumoNocturnoMensualGs),
          net_metering_efficiency: netMeteringEfficiency,
          ley_aplicable: 'Ley Nº 7599/25 - Generación Distribuida',
          medidor_bidireccional: true,
          factura_estimada: Math.round(newBill)
        };
      }
      
      setResult({
        // Capacidad del sistema
        system_capacity_kw: Math.round(actualCapacityKw * 100) / 100,
        recommended_panels: recommendedPanels,
        panel_watts: TRINA_PANEL_WATTS,
        
        // Consumo y generación
        monthly_consumption_kwh: monthlyKwh,
        energy_goal_kwh: Math.round(energyGoalKwh),
        actual_generation_kwh: Math.round(actualGenerationKwhMonth),
        remaining_consumption_kwh: Math.round(remainingKwh),
        coverage_percent: coveragePercent,
        actual_coverage_percent: Math.round((actualGenerationKwhMonth / monthlyKwh) * 100),
        daily_consumption_kwh: Math.round(dailyKwh * 10) / 10,
        
        // Tarifas ANDE
        current_tariff: currentTariff,
        new_tariff: newTariff,
        current_bill: Math.round(currentBill),
        new_bill: Math.round(newBill),
        ande_pliego: "Pliego Nro 21 - Cat. 142 Residencial",
        ande_level_name: currentTariff.level,
        ande_rate: currentTariff.rate,
        effective_rate: currentTariff.rate,
        
        // Costos
        installation_cost: Math.round(finalCost),
        cash_price: Math.round(cashPrice),
        cash_discount_percent: s.cash_discount_percent || 5,
        volume_discount_applied: volumeDiscountApplied,
        
        // Ahorros
        monthly_savings: Math.round(monthlySavings),
        annual_savings: Math.round(annualSavings),
        roi_years: Math.round(paybackYears * 10) / 10, // Payback period (años)
        roi_percent: Math.round(roiPercent * 10) / 10, // ROI porcentaje
        projection_25_years: Math.round(projection25Years),
        
        // NUEVO: Datos avanzados de ROI
        roi_data: {
          system_lifetime_years: systemLifetimeYears,
          annual_degradation_rate: annualDegradationRate * 100, // % para mostrar
          total_savings_lifetime: Math.round(totalSavingsLifetime),
          total_maintenance_cost: Math.round(totalMaintenanceCostGs),
          incentives_amount: Math.round(incentivesAmount),
          tax_credit_amount: Math.round(taxCreditAmount),
          total_incentives: Math.round(totalIncentives),
          net_system_cost: Math.round(netSystemCost),
          payback_years: Math.round(paybackYears * 10) / 10,
          roi_percent: Math.round(roiPercent * 10) / 10,
          energy_price_increase_rate: energyPriceIncreaseRate * 100 // % para mostrar
        },
        
        // Impacto ambiental
        carbon_offset_kg: Math.round(carbonOffsetKg),
        trees_equivalent: Math.round(treesEquivalent),
        water_saved_liters: Math.round(waterSavedLiters),
        
        // Garantías
        panel_warranty_years: s.panel_warranty_years || 25,
        inverter_warranty_years: s.inverter_warranty_years || 10,
        financing_max_months: s.financing_max_months || 24,
        financing_min_months: s.financing_min_months || 6,
        
        // Tipo de sistema
        system_type: systemType,
        system_description: systemDescription,
        system_recommendation: systemRecommendation,
        battery_capacity_kwh: batteryCapacityKwh,
        includes_batteries: systemType === 'independiente',
        is_ongrid: systemType === 'ongrid',
        
        // Datos On-Grid
        ongrid_data: onGridData,
        
        // Datos del cliente
        customerData: {
          name: formData.name,
          whatsapp: formData.whatsapp,
          address: formData.address,
          monthlyKwh: monthlyKwh,
          coveragePercent: coveragePercent,
          propertyType: propertyType,
          systemType: systemType
        },
        
        // Desglose de costos
        cost_breakdown: {
          panels: panelsCost,
          inverter: inverterCost,
          structure: structureCost,
          labor: laborCost,
          cabling: cablingCost,
          batteries: batteryCost,
          bidirectional_meter: bidirectionalMeterCost,
          // Información de márgenes aplicados
          base_cost_usd: Math.round(baseCostUsd),
          contingency_percent: contingencyPercent,
          gross_margin_percent: grossMarginPercent,
          final_price_usd: Math.round(finalPriceUsd),
          dollar_rate: dollarRate
        },
        
        // Desglose del cálculo
        calculation_breakdown: {
          trina_kwh_per_panel: TRINA_PANEL_KWH_MONTH,
          energy_goal: energyGoalKwh,
          panels_formula: `Math.ceil(${Math.round(energyGoalKwh)} kWh ÷ ${TRINA_PANEL_KWH_MONTH} kWh/panel) = ${recommendedPanels} paneles`
        }
      });
      
      setStep('results');

      // Guardar lead de calculadora en el backend
      try {
        await fetch(`${API_URL}/api/calculator-leads`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: formData.name,
            whatsapp: formData.whatsapp,
            address: formData.address,
            monthly_kwh: monthlyKwh,
            coverage_percent: coveragePercent,
            property_type: propertyType,
            system_type: systemType,
            tarifa_type: formData.tarifaType || 'residencial',
            recommended_panels: recommendedPanels,
            system_capacity_kw: Math.round(actualCapacityKw * 100) / 100,
            monthly_savings_gs: Math.round(monthlySavingsGs),
            installation_cost_gs: Math.round(installationCostGs),
            roi_years: Math.round(roiYears * 10) / 10
          })
        });
      } catch (err) {
        console.error('Error guardando lead de calculadora:', err);
      }
    } catch (error) {
      console.error('Error calculating:', error);
      alert('Error al calcular. Por favor intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  // Calculate long-term savings with energy price increases
  const calculateLongTermSavings = (annualSavings, increaseRate, years) => {
    let total = 0;
    let currentSavings = annualSavings;
    for (let i = 0; i < years; i++) {
      total += currentSavings;
      currentSavings *= (1 + increaseRate / 100);
    }
    return total;
  };

  const getSystemImage = (panels) => {
    if (panels <= 15) return systemImages.small;
    if (panels <= 25) return systemImages.medium;
    if (panels <= 40) return systemImages.large;
    if (panels <= 60) return systemImages.xlarge;
    return systemImages.xxlarge;
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-PY', {
      style: 'currency',
      currency: 'PYG',
      minimumFractionDigits: 0
    }).format(value);
  };

  const formatCurrencyShort = (value) => {
    if (value >= 1000000) {
      return `Gs. ${(value / 1000000).toFixed(1)}M`;
    }
    return new Intl.NumberFormat('es-PY', {
      style: 'currency',
      currency: 'PYG',
      minimumFractionDigits: 0
    }).format(value);
  };

  const calculateMonthlyPayment = (total, months) => {
    return total / months;
  };

  const handleShareBudget = async () => {
    // Create shareable data
    const shareData = {
      budgetId,
      name: result?.customerData?.name,
      system: `${result?.system_capacity_kw} kW`,
      panels: result?.recommended_panels,
      cost: formatCurrency(result?.installation_cost),
      monthlySavings: formatCurrency(result?.monthly_savings),
      annualSavings: formatCurrency(result?.annual_savings),
      roi: result?.roi_years
    };
    
    // Encode data for URL
    const encodedData = btoa(encodeURIComponent(JSON.stringify(shareData)));
    const shareUrl = `${window.location.origin}/calculadora?budget=${encodedData}`;
    
    const shareText = `🌞 Mi Presupuesto Solar Zenex #${budgetId}
    
📊 Sistema: ${result?.system_capacity_kw} kW (${result?.recommended_panels} paneles)
💰 Inversión: ${formatCurrency(result?.installation_cost)}
💚 Ahorro mensual: ${formatCurrency(result?.monthly_savings)}
⏱️ ROI: ${result?.roi_years} años

¡Calculá tu propio sistema en:`;

    if (navigator.share) {
      try {
        await navigator.share({
          title: `Presupuesto Solar #${budgetId}`,
          text: shareText,
          url: shareUrl
        });
      } catch (err) {
        // User cancelled or error
        copyToClipboard(shareUrl);
      }
    } else {
      copyToClipboard(shareUrl);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert('¡Enlace copiado! Compartilo con quien quieras.');
  };

  const handleDownloadPDF = () => {
    // Create a simple text-based "receipt" that can be shared
    const pdfContent = `
═══════════════════════════════════════
       PRESUPUESTO SOLAR ZENEX
           #${budgetId}
═══════════════════════════════════════

CLIENTE: ${result?.customerData?.name}
DIRECCIÓN: ${result?.customerData?.address}

───────────────────────────────────────
         SISTEMA RECOMENDADO
───────────────────────────────────────

• Capacidad: ${result?.system_capacity_kw} kW
• Paneles: ${result?.recommended_panels} x 450W Premium
• Inversor de Alta Eficiencia
• Estructura y Cableado
• Instalación Certificada
• 25 años de Garantía
• Monitoreo en Tiempo Real

───────────────────────────────────────
            TU AHORRO
───────────────────────────────────────

Ahorro Mensual: ${formatCurrency(result?.monthly_savings)}
Ahorro Anual: ${formatCurrency(result?.annual_savings)}
Retorno de Inversión: ${result?.roi_years} años

───────────────────────────────────────
           INVERSIÓN
───────────────────────────────────────

TOTAL: ${formatCurrency(result?.installation_cost)}

───────────────────────────────────────
        IMPACTO AMBIENTAL
───────────────────────────────────────

CO₂ evitado: ${result?.carbon_offset_kg?.toLocaleString('es-PY')} kg/año
Equivalente a: ${Math.round(result?.carbon_offset_kg / 20)} árboles por año

═══════════════════════════════════════
         ZENEX ENERGÍA SOLAR
    WhatsApp: +595 991 344 956
═══════════════════════════════════════

* Presupuesto estimado sujeto a visita técnica gratuita
`;

    // Create blob and download
    const blob = new Blob([pdfContent], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Presupuesto_Zenex_${budgetId}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleWhatsAppContact = (type = 'general') => {
    const phone = settings?.whatsapp_number?.replace(/\D/g, '') || '595991344956';
    let message = '';
    
    if (type === 'financing') {
      message = `Hola Zenex! 👋

Quiero *FINANCIAR* mi sistema solar.

📋 *Presupuesto #${budgetId}*
👤 Nombre: ${result?.customerData?.name}
📍 Dirección: ${result?.customerData?.address}

⚡ Sistema: ${result?.system_capacity_kw} kW (${result?.recommended_panels} paneles solares)
💰 Inversión: ${formatCurrency(result?.installation_cost)}
📅 Plan elegido: *${installments} cuotas* de ${formatCurrency(calculateMonthlyPayment(result?.installation_cost, installments))}

¡Espero su contacto!`;
    } else if (type === 'cash') {
      message = `Hola Zenex! 👋

Quiero comprar *AL CONTADO* mi sistema solar.

📋 *Presupuesto #${budgetId}*
👤 Nombre: ${result?.customerData?.name}
📍 Dirección: ${result?.customerData?.address}

⚡ Sistema: ${result?.system_capacity_kw} kW (${result?.recommended_panels} paneles solares)
💰 Precio contado: ${formatCurrency(result?.cash_price)} (${result?.cash_discount_percent}% descuento)

¿Hay algún descuento adicional disponible?`;
    } else {
      message = `Hola Zenex! Me interesa el presupuesto #${budgetId}. Mi nombre es ${result?.customerData?.name}.`;
    }
    
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(message)}`, '_blank');
  };

  const openPopup = (type) => {
    setPopupType(type);
    setShowPopup(true);
  };

  // ==================== RESULTS VIEW ====================
  if (step === 'results' && result) {
    const monthlyPayment = calculateMonthlyPayment(result.installation_cost, installments);

    return (
      <div className="min-h-screen bg-zinc-50">
        <Navigation isScrolled={isScrolled} />
        
        {/* Mobile App-like Container */}
        <div className="pt-20 pb-6 px-3 sm:pt-28 sm:pb-12 sm:px-6">
          <div className="max-w-4xl mx-auto">
            
            {/* Header - Mobile Optimized */}
            <div className="text-center mb-4 sm:mb-8">
              <div className="inline-block bg-zinc-900 text-white px-3 py-1.5 rounded-full text-xs font-bold mb-2 sm:px-4 sm:py-2 sm:text-sm sm:mb-3">
                Presupuesto #{budgetId}
              </div>
              <h1 className="text-2xl sm:text-4xl md:text-5xl font-bold text-zinc-900 mb-1 sm:mb-2">
                Tu Sistema Solar
              </h1>
              <p className="text-xs sm:text-sm text-zinc-500">
                Estimación • Sujeto a visita técnica gratuita
              </p>
            </div>

            {/* Main Card - Mobile App Style */}
            <div className="bg-white rounded-2xl sm:rounded-3xl shadow-xl overflow-hidden border border-zinc-100">
              
              {/* System Image - Compact on Mobile */}
              <div className="relative h-40 sm:h-64 lg:h-80">
                <img
                  src={getSystemImage(result.recommended_panels)}
                  alt="Sistema Solar"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
                <div className="absolute bottom-3 left-3 right-3 sm:bottom-6 sm:left-6 sm:right-6">
                  <div className="flex flex-wrap gap-2 mb-1.5 sm:mb-2">
                    <div className="bg-[#90EE90] text-zinc-900 px-2.5 py-1 rounded-md inline-block font-bold text-[10px] sm:text-xs sm:px-3 sm:py-1.5">
                      {result.system_description || 'Sistema Recomendado'}
                    </div>
                    {result.includes_batteries && (
                      <div className="bg-green-600 text-white px-2.5 py-1 rounded-md inline-block font-bold text-[10px] sm:text-xs sm:px-3 sm:py-1.5">
                        Con Baterías
                      </div>
                    )}
                    {result.is_ongrid && (
                      <div className="bg-blue-500 text-white px-2.5 py-1 rounded-md inline-block font-bold text-[10px] sm:text-xs sm:px-3 sm:py-1.5">
                        Ley 7599/25
                      </div>
                    )}
                    {result.is_ongrid && (
                      <div className="bg-yellow-400 text-zinc-900 px-2.5 py-1 rounded-md inline-block font-bold text-[10px] sm:text-xs sm:px-3 sm:py-1.5">
                        Factura Cero
                      </div>
                    )}
                  </div>
                  <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-white">
                    {result.system_capacity_kw} kW
                  </h2>
                  <p className="text-white/80 text-xs sm:text-sm">
                    {result.recommended_panels} Paneles Solares
                    {result.includes_batteries && ` • ${result.battery_capacity_kwh} kWh Baterías`}
                    {result.is_ongrid && ` • Medidor Bidireccional`}
                  </p>
                </div>
              </div>

              {/* Content - Mobile Optimized Padding */}
              <div className="p-4 sm:p-6 lg:p-8">
                
                {/* Customer Info - Compact */}
                <div className="mb-4 pb-3 border-b border-zinc-100 sm:mb-6 sm:pb-4">
                  <p className="text-[10px] sm:text-xs text-zinc-400 uppercase tracking-wider mb-0.5">Cliente</p>
                  <p className="font-semibold text-zinc-900 text-sm sm:text-base">{result.customerData.name}</p>
                  <p className="text-xs sm:text-sm text-zinc-500 truncate">{result.customerData.address}</p>
                </div>

                {/* Savings Highlight - Mobile Card */}
                <div className="bg-gradient-to-br from-[#90EE90]/20 to-[#90EE90]/5 border border-[#90EE90]/40 rounded-xl p-4 mb-4 sm:p-6 sm:mb-6">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="p-1.5 bg-[#90EE90]/30 rounded-lg sm:p-2">
                      <TrendingUp className="text-[#14532D] w-4 h-4 sm:w-5 sm:h-5" />
                    </div>
                    <span className="font-bold text-zinc-900 text-sm sm:text-base">Tu Ahorro con Zenex</span>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3 sm:gap-4">
                    <div>
                      <p className="text-[10px] sm:text-xs text-zinc-500 mb-0.5">Ahorro Mensual</p>
                      <p className="text-xl sm:text-3xl font-bold text-zinc-900">{formatCurrencyShort(result.monthly_savings)}</p>
                    </div>
                    <div>
                      <p className="text-[10px] sm:text-xs text-zinc-500 mb-0.5">Ahorro Anual</p>
                      <p className="text-xl sm:text-3xl font-bold text-zinc-900">{formatCurrencyShort(result.annual_savings)}</p>
                    </div>
                  </div>
                  
                  <div className="mt-3 pt-3 border-t border-[#90EE90]/30 sm:mt-4 sm:pt-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-[10px] sm:text-xs text-zinc-500">Retorno de Inversión</p>
                        <p className="text-lg sm:text-2xl font-bold text-[#14532D]">{result.roi_years} años</p>
                      </div>
                      <div className={`px-2 py-1 rounded-lg text-[10px] sm:text-xs font-bold sm:px-3 sm:py-1.5 ${
                        result.roi_years <= 5 ? 'bg-[#90EE90] text-zinc-900' :
                        result.roi_years <= 8 ? 'bg-yellow-400 text-zinc-900' :
                        'bg-orange-400 text-white'
                      }`}>
                        {result.roi_years <= 5 ? '¡Excelente!' :
                         result.roi_years <= 8 ? '¡Muy bueno!' :
                         'Buena inversión'}
                      </div>
                    </div>
                  </div>
                </div>

                {/* System Recommendation */}
                {result.system_recommendation && (
                  <div className="bg-blue-50 border border-blue-100 rounded-xl p-3 mb-4 sm:p-4 sm:mb-6">
                    <div className="flex items-start gap-2">
                      <Info className="text-blue-500 flex-shrink-0 w-4 h-4 mt-0.5" />
                      <div>
                        <p className="text-xs sm:text-sm font-semibold text-blue-900 mb-1">Recomendación Técnica</p>
                        <p className="text-[10px] sm:text-xs text-blue-700">{result.system_recommendation}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Consumption Info - Compact */}
                <div className="bg-zinc-50 border border-zinc-200 rounded-xl p-3 mb-4 sm:p-4 sm:mb-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-zinc-500 text-[10px] sm:text-xs">Consumo mensual estimado</p>
                      <p className="text-zinc-900 font-bold text-base sm:text-lg">{result.monthly_consumption_kwh} kWh</p>
                    </div>
                    <div className="text-right">
                      <p className="text-zinc-500 text-[10px] sm:text-xs">Nivel ANDE</p>
                      <p className="text-[#14532D] font-bold text-sm sm:text-base">{result.ande_level_name}</p>
                    </div>
                  </div>
                  <div className="mt-2 pt-2 border-t border-zinc-200">
                    <p className="text-zinc-600 text-[10px] sm:text-xs">
                      Tarifa aplicada: <span className="font-bold text-[#14532D]">{result.ande_rate?.toFixed(2)} Gs/kWh</span>
                      <span className="text-zinc-400 ml-2">({result.ande_pliego})</span>
                    </p>
                  </div>
                </div>

                {/* ==================== DESGLOSE TÉCNICO PROFESIONAL ==================== */}
                <div className="mb-6 sm:mb-8">
                  {/* Panel principal con paleta corporativa */}
                  <div className="relative overflow-hidden rounded-2xl bg-zinc-900 border border-zinc-800">
                    {/* Grid decorativo sutil */}
                    <div className="absolute inset-0 opacity-5" style={{
                      backgroundImage: 'linear-gradient(#90EE90 1px, transparent 1px), linear-gradient(90deg, #90EE90 1px, transparent 1px)',
                      backgroundSize: '40px 40px'
                    }}></div>
                    
                    {/* Header */}
                    <div className="relative border-b border-zinc-800 px-5 py-4 sm:px-6 sm:py-5">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-[#90EE90] text-[10px] sm:text-xs font-mono tracking-wider uppercase">Análisis Técnico</p>
                          <h3 className="text-white text-base sm:text-lg font-bold mt-0.5">Dimensionamiento del Sistema</h3>
                        </div>
                        <div className="text-right">
                          <p className="text-zinc-500 text-[9px] sm:text-[10px]">Tarifa ANDE</p>
                          <p className="text-white font-mono text-sm sm:text-base">{result.ande_level_name || 'Residencial'}</p>
                        </div>
                      </div>
                    </div>

                    {/* Contenido principal */}
                    <div className="relative p-5 sm:p-6">
                      
                      {/* RECOMENDACIÓN DEL EQUIPO */}
                      <div className="bg-zinc-800/80 rounded-xl p-4 sm:p-5 mb-6 border border-[#90EE90]/30">
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 rounded-lg bg-[#90EE90]/20 flex items-center justify-center flex-shrink-0">
                            <Sun className="w-5 h-5 text-[#90EE90]" />
                          </div>
                          <div>
                            <p className="text-[#90EE90] text-[10px] sm:text-xs font-bold uppercase tracking-wider mb-1">Recomendación de Equipo</p>
                            <p className="text-white text-sm sm:text-base leading-relaxed">
                              {result.system_recommendation}
                            </p>
                          </div>
                        </div>
                      </div>
                      
                      {/* COMPARACIÓN DE FACTURAS - Estilo Dashboard */}
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                        {/* Factura Actual */}
                        <div className="bg-red-500/10 rounded-xl p-4 sm:p-5 border border-red-500/30">
                          <div className="flex items-center gap-2 mb-2">
                            <div className="w-2 h-2 rounded-full bg-red-500"></div>
                            <p className="text-red-400 text-[10px] sm:text-xs font-bold uppercase">Factura Actual</p>
                          </div>
                          <p className="text-white text-2xl sm:text-3xl font-black">{formatCurrency(result.current_bill)}</p>
                          <p className="text-red-400/70 text-[10px] sm:text-xs mt-1">
                            {result.monthly_consumption_kwh} kWh × {result.current_tariff?.rate} Gs
                          </p>
                        </div>
                        
                        {/* Flecha */}
                        <div className="hidden sm:flex items-center justify-center">
                          <div className="flex flex-col items-center">
                            <ArrowRight className="w-8 h-8 text-[#90EE90]" />
                            <p className="text-[#90EE90] text-[10px] font-bold mt-1">CON SOLAR</p>
                          </div>
                        </div>
                        
                        {/* Nueva Factura */}
                        <div className="bg-[#14532D]/30 rounded-xl p-4 sm:p-5 border border-[#90EE90]/50">
                          <div className="flex items-center gap-2 mb-2">
                            <div className="w-2 h-2 rounded-full bg-[#90EE90]"></div>
                            <p className="text-[#90EE90] text-[10px] sm:text-xs font-bold uppercase">Nueva Factura ANDE</p>
                          </div>
                          <p className="text-white text-2xl sm:text-3xl font-black">
                            {result.new_bill <= 0 ? 'Gs. 0' : formatCurrency(result.new_bill)}
                          </p>
                          <p className="text-[#90EE90]/70 text-[10px] sm:text-xs mt-1">
                            {result.remaining_consumption_kwh || 0} kWh × {result.new_tariff?.rate || 0} Gs
                          </p>
                        </div>
                      </div>
                      
                      {/* AHORRO MENSUAL - Destacado */}
                      <div className="bg-gradient-to-r from-yellow-500/20 to-orange-500/20 rounded-xl p-4 sm:p-5 border border-yellow-500/30 mb-6">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-yellow-400 text-[10px] sm:text-xs font-bold uppercase tracking-wider">Ahorro Mensual</p>
                            <p className="text-white text-3xl sm:text-4xl font-black mt-1">{formatCurrency(result.monthly_savings)}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-yellow-400 text-[10px] sm:text-xs font-bold uppercase tracking-wider">Ahorro Anual</p>
                            <p className="text-yellow-400 text-xl sm:text-2xl font-bold mt-1">{formatCurrency(result.annual_savings)}</p>
                          </div>
                        </div>
                      </div>
                      
                      {/* Datos técnicos */}
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-6">
                        <div className="bg-zinc-800/50 rounded-xl p-3 sm:p-4 border border-zinc-700/50">
                          <p className="text-zinc-500 text-[9px] sm:text-[10px] uppercase tracking-wide mb-1">Consumo</p>
                          <p className="text-white font-bold text-sm sm:text-lg">{result.monthly_consumption_kwh} <span className="text-zinc-400 text-xs">kWh</span></p>
                        </div>
                        <div className="bg-zinc-800/50 rounded-xl p-3 sm:p-4 border border-zinc-700/50">
                          <p className="text-zinc-500 text-[9px] sm:text-[10px] uppercase tracking-wide mb-1">Generación</p>
                          <p className="text-[#90EE90] font-bold text-sm sm:text-lg">{result.actual_generation_kwh} <span className="text-zinc-400 text-xs">kWh</span></p>
                        </div>
                        <div className="bg-zinc-800/50 rounded-xl p-3 sm:p-4 border border-zinc-700/50">
                          <p className="text-zinc-500 text-[9px] sm:text-[10px] uppercase tracking-wide mb-1">Tarifa Nivel</p>
                          <p className="text-white font-bold text-xs sm:text-sm">{result.current_tariff?.level}</p>
                        </div>
                        <div className="bg-zinc-800/50 rounded-xl p-3 sm:p-4 border border-zinc-700/50">
                          <p className="text-zinc-500 text-[9px] sm:text-[10px] uppercase tracking-wide mb-1">Precio/kWh</p>
                          <p className="text-white font-bold text-sm sm:text-lg">{result.current_tariff?.rate} <span className="text-zinc-400 text-xs">Gs</span></p>
                        </div>
                      </div>

                      {/* Separador con fórmula del cálculo */}
                      <div className="flex items-center gap-4 mb-6">
                        <div className="flex-1 h-px bg-zinc-700"></div>
                        <div className="bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2">
                          <p className="text-zinc-400 text-[9px] sm:text-[10px] font-mono text-center">
                            {result.calculation_breakdown?.panels_formula || `Paneles = ${result.energy_goal_kwh} kWh ÷ 88.8 kWh/panel`}
                          </p>
                        </div>
                        <div className="flex-1 h-px bg-zinc-700"></div>
                      </div>

                      {/* Resultado del sistema */}
                      <div className="bg-gradient-to-r from-[#14532D] to-[#166534] rounded-xl p-4 sm:p-6 mb-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-[#90EE90]/70 text-[10px] sm:text-xs uppercase tracking-wider mb-1">Sistema Recomendado</p>
                            <div className="flex items-baseline gap-2">
                              <p className="text-white text-3xl sm:text-4xl font-black">{result.system_capacity_kw}</p>
                              <p className="text-[#90EE90] text-lg sm:text-xl font-bold">kW</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-[#90EE90]/70 text-[10px] sm:text-xs uppercase tracking-wider mb-1">Cobertura</p>
                            <div className="flex items-baseline gap-1 justify-end">
                              <p className="text-white text-3xl sm:text-4xl font-black">{result.coverage_percent}</p>
                              <p className="text-[#90EE90] text-lg sm:text-xl font-bold">%</p>
                            </div>
                          </div>
                        </div>
                        
                        {/* Barra de progreso */}
                        <div className="mt-4">
                          <div className="h-1.5 sm:h-2 bg-black/30 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-[#90EE90] rounded-full transition-all duration-1000"
                              style={{ width: `${result.coverage_percent}%` }}
                            ></div>
                          </div>
                        </div>
                      </div>

                      {/* Componentes del sistema */}
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
                        <div className="bg-zinc-800/50 rounded-xl p-3 sm:p-4 border border-zinc-700/50 text-center">
                          <div className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-2 rounded-lg bg-zinc-700/50 flex items-center justify-center">
                            <Zap className="w-5 h-5 sm:w-6 sm:h-6 text-[#90EE90]" />
                          </div>
                          <p className="text-white font-bold text-base sm:text-lg">{result.recommended_panels}</p>
                          <p className="text-zinc-500 text-[9px] sm:text-[10px]">Paneles Solares</p>
                        </div>
                        
                        {result.includes_batteries && (
                          <div className="bg-zinc-800/50 rounded-xl p-3 sm:p-4 border border-zinc-700/50 text-center">
                            <div className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-2 rounded-lg bg-zinc-700/50 flex items-center justify-center">
                              <Battery className="w-5 h-5 sm:w-6 sm:h-6 text-[#90EE90]" />
                            </div>
                            <p className="text-white font-bold text-base sm:text-lg">{result.battery_capacity_kwh}</p>
                            <p className="text-zinc-500 text-[9px] sm:text-[10px]">kWh Baterías</p>
                          </div>
                        )}
                        
                        <div className="bg-zinc-800/50 rounded-xl p-3 sm:p-4 border border-zinc-700/50 text-center">
                          <div className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-2 rounded-lg bg-zinc-700/50 flex items-center justify-center">
                            <TrendingUp className="w-5 h-5 sm:w-6 sm:h-6 text-[#90EE90]" />
                          </div>
                          <p className="text-white font-bold text-base sm:text-lg">{result.roi_years}</p>
                          <p className="text-zinc-500 text-[9px] sm:text-[10px]">Años retorno</p>
                        </div>
                        
                        <div className="bg-zinc-800/50 rounded-xl p-3 sm:p-4 border border-zinc-700/50 text-center">
                          <div className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-2 rounded-lg bg-zinc-700/50 flex items-center justify-center">
                            <Leaf className="w-5 h-5 sm:w-6 sm:h-6 text-[#90EE90]" />
                          </div>
                          <p className="text-white font-bold text-base sm:text-lg">{(result.carbon_offset_kg / 1000).toFixed(1)}t</p>
                          <p className="text-zinc-500 text-[9px] sm:text-[10px]">CO₂/año evitado</p>
                        </div>
                      </div>

                      {/* Badge tipo de sistema */}
                      <div className="flex justify-center mt-5">
                        <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs sm:text-sm font-medium border ${
                          result.system_type === 'ongrid'
                            ? 'bg-blue-500/10 text-blue-400 border-blue-500/30'
                            : 'bg-[#90EE90]/10 text-[#90EE90] border-[#90EE90]/30'
                        }`}>
                          {result.system_type === 'autoconsumo' ? (
                            <>
                              <Sun className="w-4 h-4" />
                              <span>Autoconsumo — Energía diurna directa</span>
                            </>
                          ) : result.system_type === 'ongrid' ? (
                            <>
                              <ArrowLeftRight className="w-4 h-4" />
                              <span>On-Grid — Inyección a red ANDE (Ley 7599/25)</span>
                            </>
                          ) : (
                            <>
                              <Battery className="w-4 h-4" />
                              <span>Independiente — Energía 24/7 con almacenamiento</span>
                            </>
                          )}
                        </div>
                      </div>
                      
                      {/* Panel especial On-Grid con detalles de la Ley 7599/25 */}
                      {result.is_ongrid && result.ongrid_data && (
                        <div className="mt-5 bg-blue-500/10 border border-blue-500/30 rounded-xl p-4 sm:p-5">
                          <div className="flex items-center gap-2 mb-3">
                            <PlugZap className="w-5 h-5 text-blue-400" />
                            <h4 className="text-white font-bold text-sm sm:text-base">Balance Net Metering</h4>
                          </div>
                          
                          <div className="grid grid-cols-2 gap-3 mb-4">
                            <div className="bg-zinc-800/50 rounded-lg p-3">
                              <p className="text-zinc-500 text-[9px] sm:text-[10px] uppercase mb-1">Consumo Diurno</p>
                              <p className="text-white font-bold text-sm sm:text-base">{result.ongrid_data.consumo_diurno_kwh} kWh</p>
                              <p className="text-zinc-400 text-[9px] sm:text-[10px]">40% del total</p>
                            </div>
                            <div className="bg-zinc-800/50 rounded-lg p-3">
                              <p className="text-zinc-500 text-[9px] sm:text-[10px] uppercase mb-1">Consumo Nocturno</p>
                              <p className="text-white font-bold text-sm sm:text-base">{result.ongrid_data.consumo_nocturno_kwh} kWh</p>
                              <p className="text-zinc-400 text-[9px] sm:text-[10px]">60% del total</p>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-3 mb-4">
                            <div className="flex-1 bg-[#14532D]/30 rounded-lg p-3 text-center">
                              <p className="text-[#90EE90]/70 text-[9px] sm:text-[10px] uppercase mb-1">Generación Diaria</p>
                              <p className="text-[#90EE90] font-bold text-lg sm:text-xl">{result.ongrid_data.generacion_diaria_kwh} kWh</p>
                            </div>
                            <ArrowRight className="text-zinc-500 w-5 h-5 flex-shrink-0" />
                            <div className="flex-1 bg-yellow-500/20 rounded-lg p-3 text-center">
                              <p className="text-yellow-400/70 text-[9px] sm:text-[10px] uppercase mb-1">Excedente a ANDE</p>
                              <p className="text-yellow-400 font-bold text-lg sm:text-xl">{result.ongrid_data.excedente_diario_kwh} kWh</p>
                            </div>
                          </div>
                          
                          <div className="bg-zinc-900/50 rounded-lg p-3 border border-zinc-700/50">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-zinc-400 text-xs">Crédito mensual generado:</span>
                              <span className="text-[#90EE90] font-bold">{new Intl.NumberFormat('es-PY').format(result.ongrid_data.credito_mensual_gs)} Gs</span>
                            </div>
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-zinc-400 text-xs">Consumo nocturno mensual:</span>
                              <span className="text-orange-400 font-bold">-{new Intl.NumberFormat('es-PY').format(result.ongrid_data.consumo_nocturno_mensual_gs)} Gs</span>
                            </div>
                            <div className="pt-2 border-t border-zinc-700 flex items-center justify-between">
                              <span className="text-white font-bold text-sm">Factura estimada:</span>
                              <span className={`font-black text-lg ${result.ongrid_data.factura_estimada <= 20000 ? 'text-[#90EE90]' : 'text-yellow-400'}`}>
                                {result.ongrid_data.factura_estimada <= 0 ? 'Gs. 0' : new Intl.NumberFormat('es-PY', { style: 'currency', currency: 'PYG', minimumFractionDigits: 0 }).format(result.ongrid_data.factura_estimada)}
                              </span>
                            </div>
                          </div>
                          
                          <p className="text-zinc-500 text-[9px] sm:text-[10px] mt-3 text-center">
                            {result.ongrid_data.ley_aplicable} • Requiere medidor bidireccional
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Footer con referencia ANDE */}
                    <div className="border-t border-zinc-800 px-5 py-3 sm:px-6 sm:py-4">
                      <p className="text-zinc-500 text-[9px] sm:text-[10px] text-center">
                        Cálculo basado en {result.ande_pliego} • Nivel tarifario: {result.ande_level_name} ({result.ande_rate?.toFixed(2)} Gs/kWh)
                      </p>
                    </div>
                  </div>
                </div>

                {/* What's Included - Compact List */}
                <div className="mb-4 sm:mb-6">
                  <h4 className="font-bold text-zinc-900 mb-2 text-sm sm:text-base sm:mb-3">¿Qué incluye?</h4>
                  <div className="grid grid-cols-1 gap-1.5 sm:gap-2">
                    {[
                      `${result.recommended_panels} Paneles Solares de Alta Eficiencia`,
                      result.includes_batteries ? 'Inversor Híbrido Trifásico' : result.is_ongrid ? 'Inversor On-Grid Certificado' : 'Inversor de Alta Eficiencia',
                      result.includes_batteries ? `Baterías de Litio ${result.battery_capacity_kwh} kWh` : null,
                      result.is_ongrid ? 'Medidor Bidireccional Homologado ANDE' : null,
                      result.is_ongrid ? 'Gestoría de Conexión a Red (Ley 7599/25)' : null,
                      'Estructura y Cableado Certificado',
                      'Instalación Profesional',
                      `${result.panel_warranty_years} años de Garantía Paneles`,
                      'Monitoreo en Tiempo Real',
                      'Pozo a Tierra (PAT) incluido'
                    ].filter(Boolean).map((item, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <Check className="text-[#90EE90] w-3.5 h-3.5 flex-shrink-0 sm:w-4 sm:h-4" />
                        <span className="text-xs sm:text-sm text-zinc-700">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Análisis Financiero Simplificado */}
                {result.roi_data && (
                  <div className="bg-gradient-to-br from-emerald-50 to-green-50 border border-emerald-200 rounded-xl p-4 mb-4 sm:p-5 sm:mb-6" data-testid="roi-analysis-section">
                    <div className="flex items-center gap-2 mb-4">
                      <TrendingUp className="text-emerald-600 w-5 h-5" />
                      <h4 className="font-bold text-zinc-900 text-sm sm:text-base">Análisis de Inversión</h4>
                    </div>
                    
                    {/* Resumen simplificado */}
                    <div className="grid grid-cols-3 gap-3 mb-4">
                      <div className="bg-white/80 rounded-lg p-3 text-center border border-emerald-100">
                        <p className="text-emerald-600 text-[9px] sm:text-[10px] uppercase font-bold mb-1">Retorno</p>
                        <p className="text-zinc-900 font-black text-lg sm:text-xl">{result.roi_data.payback_years} <span className="text-xs text-zinc-500">años</span></p>
                      </div>
                      <div className="bg-white/80 rounded-lg p-3 text-center border border-emerald-100">
                        <p className="text-emerald-600 text-[9px] sm:text-[10px] uppercase font-bold mb-1">Ahorro Total ({result.roi_data.system_lifetime_years} años)</p>
                        <p className="text-zinc-900 font-black text-sm sm:text-base">{formatCurrencyShort(result.roi_data.total_savings_lifetime)}</p>
                      </div>
                      <div className="bg-white/80 rounded-lg p-3 text-center border border-emerald-100">
                        <p className="text-emerald-600 text-[9px] sm:text-[10px] uppercase font-bold mb-1">Ganancia Neta</p>
                        <p className="text-emerald-700 font-black text-sm sm:text-base">
                          {formatCurrencyShort(result.roi_data.total_savings_lifetime - result.roi_data.net_system_cost)}
                        </p>
                      </div>
                    </div>
                    
                    {/* Desglose del cálculo */}
                    <div className="bg-white/60 rounded-lg p-3 sm:p-4 border border-emerald-100">
                      <p className="text-[10px] sm:text-xs text-zinc-500 font-bold uppercase mb-3">Desglose</p>
                      
                      <div className="space-y-2 text-xs sm:text-sm">
                        <div className="flex justify-between">
                          <span className="text-zinc-600">Inversión inicial:</span>
                          <span className="font-bold text-zinc-900">{formatCurrency(result.installation_cost)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-zinc-600">Ahorro anual estimado:</span>
                          <span className="font-bold text-emerald-600">{formatCurrency(result.annual_savings)}</span>
                        </div>
                        <div className="pt-2 border-t border-emerald-200 flex justify-between">
                          <span className="font-bold text-zinc-700">Tiempo de retorno:</span>
                          <span className="font-black text-[#14532D]">{result.roi_data.payback_years} años</span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Notas importantes */}
                    <div className="mt-3 flex items-start gap-2">
                      <Info className="text-emerald-500 w-4 h-4 flex-shrink-0 mt-0.5" />
                      <p className="text-[9px] sm:text-[10px] text-zinc-600">
                        Cálculo considera degradación de paneles ({result.roi_data.annual_degradation_rate}%/año) y aumento de tarifas eléctricas ({result.roi_data.energy_price_increase_rate}%/año).
                      </p>
                    </div>
                  </div>
                )}

                {/* Environmental Impact - Compact */}
                <div className="bg-green-50 border border-green-100 rounded-lg p-3 mb-4 sm:p-4 sm:mb-6">
                  <div className="flex items-center gap-2">
                    <Leaf className="text-green-600 w-4 h-4 sm:w-5 sm:h-5" />
                    <div>
                      <p className="font-semibold text-green-900 text-xs sm:text-sm">Impacto Ambiental</p>
                      <p className="text-[10px] sm:text-xs text-green-700">
                        -{result.carbon_offset_kg.toLocaleString('es-PY')} kg CO₂/año • = {Math.round(result.carbon_offset_kg / 20)} árboles por año
                      </p>
                    </div>
                  </div>
                </div>

                {/* Pricing Section - Mobile Optimized */}
                <div className="bg-gradient-to-br from-zinc-900 to-zinc-800 rounded-xl p-4 mb-4 sm:p-6 sm:mb-6">
                  <div className="flex items-center justify-between mb-3 sm:mb-4">
                    <div>
                      <p className="text-[10px] sm:text-xs text-zinc-400">Inversión Total</p>
                      <p className="text-2xl sm:text-3xl font-bold text-white">{formatCurrency(result.installation_cost)}</p>
                    </div>
                  </div>
                  
                  {/* Installments Selector - Mobile Friendly */}
                  <div className="bg-white/10 rounded-lg p-3 sm:p-4">
                    <p className="text-[10px] sm:text-xs text-zinc-300 mb-2 sm:mb-3">Financiamiento sin intereses</p>
                    
                    {/* Installment Options */}
                    <div className="grid grid-cols-4 gap-1.5 sm:gap-2 mb-3">
                      {[6, 12, 18, 24].map((months) => (
                        <button
                          key={months}
                          onClick={() => setInstallments(months)}
                          className={`py-2 sm:py-2.5 rounded-lg text-xs sm:text-sm font-bold transition-all ${
                            installments === months
                              ? 'bg-[#90EE90] text-zinc-900'
                              : 'bg-zinc-700 text-zinc-300 hover:bg-zinc-600'
                          }`}
                        >
                          {months}
                        </button>
                      ))}
                    </div>
                    <p className="text-center text-[9px] sm:text-xs text-zinc-500 mb-3">meses</p>

                    {/* Monthly Payment Display */}
                    <div className="bg-[#90EE90] text-zinc-900 rounded-lg p-3 text-center sm:p-4">
                      <p className="text-[10px] sm:text-xs font-medium mb-0.5">{installments} cuotas de</p>
                      <p className="text-2xl sm:text-3xl font-bold">{formatCurrency(monthlyPayment)}</p>
                      <p className="text-[9px] sm:text-xs opacity-70 mt-0.5">Sin intereses</p>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="grid grid-cols-2 gap-2 mt-3 sm:gap-3 sm:mt-4">
                    <button
                      onClick={() => openPopup('financing')}
                      className="bg-[#90EE90] text-zinc-900 px-3 py-2.5 rounded-lg font-bold text-xs hover:bg-[#7CDF7C] transition-all sm:px-4 sm:py-3 sm:text-sm"
                    >
                      Financiar
                    </button>
                    <button
                      onClick={() => openPopup('cash')}
                      className="bg-white text-zinc-900 px-3 py-2.5 rounded-lg font-bold text-xs hover:bg-zinc-100 transition-all sm:px-4 sm:py-3 sm:text-sm"
                    >
                      Al Contado
                    </button>
                  </div>
                </div>

                {/* Disclaimer - Compact */}
                <div className="bg-blue-50 border-l-3 border-blue-400 rounded-r-lg p-3 mb-4 sm:p-4 sm:mb-6">
                  <div className="flex items-start gap-2">
                    <Info className="text-blue-500 flex-shrink-0 w-4 h-4 mt-0.5" />
                    <p className="text-[10px] sm:text-xs text-blue-800">
                      Presupuesto estimado. Realizamos <strong>visita técnica gratuita</strong> para confirmar.
                    </p>
                  </div>
                </div>

                {/* Bottom Actions */}
                <div className="space-y-2 sm:space-y-3">
                  <button
                    onClick={() => handleWhatsAppContact('financing')}
                    className="w-full bg-gradient-to-r from-[#90EE90] to-[#7CDF7C] text-zinc-900 py-3 rounded-xl font-bold text-sm flex items-center justify-center gap-2 hover:shadow-lg transition-all sm:py-4 sm:text-base"
                  >
                    Solicitar Cotización
                    <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" />
                  </button>
                  
                  <div className="grid grid-cols-2 gap-2 sm:gap-3">
                    <button
                      onClick={handleShareBudget}
                      className="border border-zinc-200 text-zinc-700 py-2.5 rounded-xl font-medium text-xs flex items-center justify-center gap-1.5 hover:border-zinc-400 transition-all sm:py-3 sm:text-sm"
                    >
                      <Share2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                      Compartir
                    </button>
                    <button
                      onClick={handleDownloadPDF}
                      className="border border-zinc-200 text-zinc-700 py-2.5 rounded-xl font-medium text-xs flex items-center justify-center gap-1.5 hover:border-zinc-400 transition-all sm:py-3 sm:text-sm"
                    >
                      <Download className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                      Descargar
                    </button>
                  </div>
                  
                  <button
                    onClick={() => { setStep('form'); setBudgetId(null); }}
                    className="w-full text-zinc-500 py-2 text-xs hover:text-zinc-700 transition-all sm:text-sm"
                  >
                    Calcular otro sistema
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <Footer />
        <FloatingButtons />

        {/* Popup Modal - Mobile Optimized */}
        {showPopup && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
            <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-md p-5 sm:p-8 relative animate-slide-up">
              <button
                onClick={() => setShowPopup(false)}
                className="absolute top-3 right-3 text-zinc-400 hover:text-zinc-900 sm:top-4 sm:right-4"
              >
                <X className="w-5 h-5 sm:w-6 sm:h-6" />
              </button>

              <div className="text-center mb-5 sm:mb-6">
                <div className={`${popupType === 'financing' ? 'bg-[#90EE90]' : 'bg-zinc-900'} w-12 h-12 sm:w-16 sm:h-16 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4`}>
                  {popupType === 'financing' ? (
                    <TrendingUp className="text-zinc-900 w-6 h-6 sm:w-8 sm:h-8" />
                  ) : (
                    <CreditCard className="text-white w-6 h-6 sm:w-8 sm:h-8" />
                  )}
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-zinc-900 mb-1.5 sm:mb-2">
                  {popupType === 'financing' ? '¡Financiamiento!' : '¡Al Contado!'}
                </h3>
                <p className="text-xs sm:text-sm text-zinc-600 px-2">
                  {popupType === 'financing' 
                    ? 'Procesamos tu solicitud y te contactamos vía WhatsApp.'
                    : 'Obtén descuento especial por pago al contado.'}
                </p>
              </div>

              {popupType === 'cash' && (
                <div className="bg-[#90EE90]/10 border border-[#90EE90] rounded-lg p-3 mb-4 sm:p-4 sm:mb-6">
                  <p className="text-xs sm:text-sm font-semibold text-zinc-900 mb-0.5">Descuento Especial</p>
                  <p className="text-[10px] sm:text-xs text-zinc-700">
                    Pregunta por nuestros descuentos para pagos al contado.
                  </p>
                </div>
              )}

              <div className="space-y-2 sm:space-y-3">
                <button
                  onClick={() => handleWhatsAppContact(popupType)}
                  className="w-full bg-[#25D366] text-white py-3 sm:py-4 rounded-xl font-bold text-sm sm:text-base hover:bg-[#20BA5A] transition-all flex items-center justify-center gap-2"
                >
                  <MessageCircle className="w-4 h-4 sm:w-5 sm:h-5" />
                  {popupType === 'financing' ? 'Solicitar Financiamiento' : 'Consultar Descuento'}
                </button>
                <button
                  onClick={() => setShowPopup(false)}
                  className="w-full border border-zinc-200 text-zinc-700 py-2.5 sm:py-3 rounded-xl font-medium text-sm hover:border-zinc-400 transition-all"
                >
                  Cerrar
                </button>
              </div>

              <p className="text-[10px] sm:text-xs text-center text-zinc-400 mt-3 sm:mt-4">
                Presupuesto #{budgetId}
              </p>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ==================== FORM VIEW ====================
  return (
    <div className="min-h-screen bg-gradient-to-br from-zinc-900 via-zinc-800 to-zinc-900">
      <Navigation isScrolled={isScrolled} />
      
      {/* Mobile App-like Container */}
      <div className="pt-20 pb-6 px-3 sm:pt-28 sm:pb-12 sm:px-6">
        <div className="max-w-xl mx-auto">
          
          {/* Header - Mobile Optimized */}
          <div className="text-center mb-6 sm:mb-10">
            <div className="inline-block bg-[#90EE90] px-3 py-1.5 rounded-full text-zinc-900 font-bold text-xs mb-3 sm:px-4 sm:py-2 sm:text-sm sm:mb-4">
              Calculadora Solar
            </div>
            <h1 className="text-2xl sm:text-4xl md:text-5xl font-bold text-white mb-1 sm:mb-2">
              Calculá tu Ahorro
            </h1>
            <h2 className="text-2xl sm:text-4xl md:text-5xl font-bold text-[#90EE90] mb-2 sm:mb-3">
              con Energía Solar
            </h2>
            <p className="text-sm sm:text-lg text-zinc-400 px-4">
              Completá los datos y obtené tu presupuesto personalizado
            </p>
          </div>

          {/* Form Card - Mobile App Style */}
          <div className="bg-white rounded-2xl sm:rounded-3xl shadow-2xl overflow-hidden">
            <div className="p-4 sm:p-8">
              
              {/* CAMPO 1: Dirección con autocompletado local + Geolocalización */}
              <div className="mb-6 sm:mb-8">
                <label className="block text-xs sm:text-sm font-bold text-zinc-900 mb-2 sm:mb-3">
                  <MapPin className="w-4 h-4 inline-block mr-1 text-[#14532D]" />
                  Tu Dirección
                </label>
                <div className="relative flex gap-2">
                  <div className="relative flex-1">
                    <input
                      ref={addressInputRef}
                      type="text"
                      placeholder="Escribí tu ciudad o barrio..."
                      value={formData.address}
                      onChange={(e) => handleAddressChange(e.target.value)}
                      onFocus={() => formData.address.length >= 2 && addressSuggestions.length > 0 && setShowSuggestions(true)}
                      onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                      className="w-full px-4 py-3 sm:py-4 pl-11 pr-4 border-2 border-zinc-200 rounded-xl focus:border-[#90EE90] focus:outline-none text-zinc-900 text-sm sm:text-base"
                      data-testid="address-input"
                      autoComplete="off"
                    />
                    <MapPin className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-zinc-400" />
                    
                    {/* Dropdown de sugerencias */}
                    {showSuggestions && addressSuggestions.length > 0 && (
                      <div className="absolute z-50 left-0 right-0 top-full mt-1 bg-white border-2 border-zinc-200 rounded-xl shadow-lg overflow-hidden">
                        {addressSuggestions.map((suggestion, index) => (
                          <button
                            key={index}
                            type="button"
                            onClick={() => selectAddressSuggestion(suggestion)}
                            className="w-full px-4 py-3 text-left text-sm text-zinc-700 hover:bg-[#90EE90]/20 hover:text-zinc-900 transition-colors flex items-center gap-2 border-b border-zinc-100 last:border-b-0"
                          >
                            <MapPin className="w-4 h-4 text-[#14532D] flex-shrink-0" />
                            <span>{suggestion}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <button
                    onClick={getGeolocation}
                    disabled={geoloading}
                    className="bg-zinc-900 text-white px-4 py-3 sm:px-5 sm:py-4 rounded-xl hover:bg-zinc-800 transition-all disabled:opacity-50 flex-shrink-0 flex items-center justify-center"
                    title="Usar mi ubicación actual"
                  >
                    {geoloading ? (
                      <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                    ) : (
                      <MapPin className="w-5 h-5" />
                    )}
                  </button>
                </div>
                <p className="text-[10px] sm:text-xs text-zinc-400 mt-1.5 ml-1">
                  {formData.useGeolocation ? (
                    <span className="text-[#14532D] flex items-center gap-1">
                      <Check className="w-3 h-3" /> Ubicación obtenida automáticamente
                    </span>
                  ) : (
                    'Escribí tu ciudad o barrio, o tocá el botón GPS para ubicarte'
                  )}
                </p>
              </div>
              
              {/* CAMPO 2: Factura Mensual en Guaraníes (PRIMERO) */}
              <div className="mb-6 sm:mb-8">
                <label className="block text-xs sm:text-sm font-bold text-zinc-900 mb-2 sm:mb-3">
                  <DollarSign className="w-4 h-4 inline-block mr-1 text-yellow-500" />
                  Tu Factura Mensual de ANDE
                </label>
                
                {/* Selector de Tipo de Tarifa */}
                <div className="mb-4">
                  <p className="text-xs text-zinc-500 mb-2">Tipo de tarifa:</p>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, tarifaType: 'residencial' })}
                      className={`px-3 py-2.5 rounded-xl text-sm font-bold transition-all flex items-center justify-center gap-2 ${
                        formData.tarifaType === 'residencial'
                          ? 'bg-[#90EE90] text-[#14532D] shadow-md'
                          : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200'
                      }`}
                      data-testid="tarifa-residencial"
                    >
                      <Home className="w-4 h-4" />
                      Residencial
                    </button>
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, tarifaType: 'industria' })}
                      className={`px-3 py-2.5 rounded-xl text-sm font-bold transition-all flex items-center justify-center gap-2 ${
                        formData.tarifaType === 'industria'
                          ? 'bg-[#90EE90] text-[#14532D] shadow-md'
                          : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200'
                      }`}
                      data-testid="tarifa-industria"
                    >
                      <Building2 className="w-4 h-4" />
                      Industria
                    </button>
                  </div>
                  {formData.tarifaType === 'industria' && (
                    <p className="text-[10px] text-amber-600 mt-2 bg-amber-50 px-2 py-1 rounded-lg">
                      Tarifa única industrial: 404,97 Gs/kWh
                    </p>
                  )}
                </div>
                
                <div className="space-y-3 sm:space-y-4">
                  {/* Display de la factura en Guaraníes */}
                  <div className="bg-zinc-900 rounded-xl p-4 text-center sm:p-5">
                    <p className="text-[10px] sm:text-xs text-zinc-400 mb-1">Factura mensual actual</p>
                    <p className={`text-3xl sm:text-4xl font-bold ${getConsumptionColor(formData.monthlyKwh)}`}>
                      {formatCurrency(calculateBillFromKwh(formData.monthlyKwh, formData.tarifaType))}
                    </p>
                  </div>
                  
                  {/* Slider para la factura (basado en kWh internamente) */}
                  {/* Rango: 50 kWh (~15,578 Gs) hasta 13,800 kWh (~6,010,038 Gs) */}
                  <div className="px-1">
                    <input
                      type="range"
                      min="50"
                      max="13800"
                      step="50"
                      value={formData.monthlyKwh}
                      onChange={(e) => setFormData({ ...formData, monthlyKwh: parseInt(e.target.value) })}
                      className="w-full h-2 sm:h-3 bg-zinc-200 rounded-lg appearance-none cursor-pointer accent-[#90EE90]"
                      style={{
                        background: `linear-gradient(to right, #90EE90 0%, #90EE90 ${((formData.monthlyKwh - 50) / 13750) * 100}%, #e4e4e7 ${((formData.monthlyKwh - 50) / 13750) * 100}%, #e4e4e7 100%)`
                      }}
                      data-testid="bill-slider"
                    />
                    <div className="flex justify-between items-start text-[10px] sm:text-xs text-zinc-500 mt-2">
                      <span>{formatCurrency(calculateBillFromKwh(50, formData.tarifaType))}</span>
                      <div className="text-right">
                        <span>{formatCurrency(calculateBillFromKwh(13800, formData.tarifaType))}</span>
                        <button
                          type="button"
                          onClick={() => setShowManualBillInput(!showManualBillInput)}
                          className="block text-[9px] sm:text-[10px] text-amber-600 hover:text-amber-700 font-medium mt-0.5 hover:underline"
                          data-testid="high-bill-button"
                        >
                          ¿Más de 6M?
                        </button>
                      </div>
                    </div>
                    
                    {/* Input manual para facturas altas */}
                    {showManualBillInput && (
                      <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                        <p className="text-[10px] sm:text-xs text-amber-700 mb-2">
                          Ingresá tu factura mensual:
                        </p>
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-bold text-amber-600">Gs.</span>
                          <input
                            type="text"
                            placeholder="8.500.000"
                            value={manualBillValue}
                            onChange={(e) => {
                              const value = e.target.value.replace(/[^\d]/g, '');
                              setManualBillValue(value ? parseInt(value).toLocaleString('es-PY') : '');
                            }}
                            className="flex-1 px-2 py-1.5 border border-amber-300 rounded-lg focus:border-amber-500 focus:outline-none text-zinc-900 text-sm font-bold text-center"
                            data-testid="manual-bill-input"
                          />
                          <button
                            type="button"
                            onClick={() => {
                              const billAmount = parseInt(manualBillValue.replace(/\./g, '')) || 0;
                              if (billAmount > 0) {
                                const estimatedKwh = Math.round(billAmount / 435.51);
                                setFormData({ ...formData, monthlyKwh: Math.max(1, estimatedKwh) });
                                setShowManualBillInput(false);
                                setManualBillValue('');
                              }
                            }}
                            className="px-3 py-1.5 bg-amber-500 text-white rounded-lg font-bold text-xs hover:bg-amber-600 transition-colors"
                          >
                            OK
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Consumo en kWh (debajo, secundario) */}
                  <div className="bg-zinc-100 rounded-lg p-3 flex items-center justify-between">
                    <div>
                      <p className="text-[10px] sm:text-xs text-zinc-500">Consumo estimado:</p>
                      <p className="text-sm sm:text-base font-bold text-zinc-800">{formData.monthlyKwh.toLocaleString('es-PY')} kWh/mes</p>
                    </div>
                    <div className="text-right">
                      <p className="text-[10px] sm:text-xs text-zinc-500">Tarifa ANDE:</p>
                      <p className="text-sm sm:text-base font-bold text-[#14532D]">{getTariffRate(formData.monthlyKwh, formData.tarifaType).rate} Gs/kWh</p>
                    </div>
                  </div>
                  
                  {/* Input directo de kWh (opcional) */}
                  <div className="flex items-center gap-2 justify-center">
                    <span className="text-xs text-zinc-500">¿Sabés tu consumo exacto?</span>
                    <input
                      type="number"
                      min="1"
                      max="50000"
                      placeholder="kWh"
                      value={formData.monthlyKwh}
                      onChange={(e) => setFormData({ ...formData, monthlyKwh: Math.max(1, Math.min(50000, parseInt(e.target.value) || 1)) })}
                      className="w-24 px-2 py-1.5 border-2 border-zinc-200 rounded-lg focus:border-[#90EE90] focus:outline-none text-zinc-900 text-sm font-bold text-center"
                      data-testid="kwh-input"
                    />
                    <span className="text-xs text-zinc-600">kWh</span>
                  </div>
                </div>
              </div>
              
              {/* CAMPO 3: Porcentaje de Cobertura (Slider 10-100%) */}
              <div className="mb-6 sm:mb-8">
                <label className="block text-xs sm:text-sm font-bold text-zinc-900 mb-2 sm:mb-3">
                  <TrendingUp className="w-4 h-4 inline-block mr-1 text-[#14532D]" />
                  Porcentaje de Consumo a Cubrir
                </label>
                
                <div className="space-y-3 sm:space-y-4">
                  {/* Current Value Display */}
                  <div className="bg-gradient-to-r from-[#14532D] to-[#166534] rounded-xl p-4 text-center sm:p-5">
                    <p className="text-[10px] sm:text-xs text-white/70 mb-1">Cobertura solar</p>
                    <div className="flex items-baseline justify-center gap-1">
                      <p className="text-4xl sm:text-5xl font-black text-white">
                        {formData.coveragePercent}
                      </p>
                      <span className="text-xl sm:text-2xl text-[#90EE90] font-bold">%</span>
                    </div>
                    <p className="text-[10px] sm:text-xs text-white/70 mt-2">
                      Generarás <span className="text-[#90EE90] font-bold">{Math.round(formData.monthlyKwh * formData.coveragePercent / 100)} kWh</span> mensuales
                    </p>
                  </div>
                  
                  {/* Slider para Cobertura */}
                  <div className="px-1">
                    <input
                      type="range"
                      min="10"
                      max="100"
                      step="5"
                      value={formData.coveragePercent}
                      onChange={(e) => setFormData({ ...formData, coveragePercent: parseInt(e.target.value) })}
                      className="w-full h-3 sm:h-4 bg-zinc-200 rounded-lg appearance-none cursor-pointer"
                      style={{
                        background: `linear-gradient(to right, #14532D 0%, #90EE90 ${formData.coveragePercent}%, #e4e4e7 ${formData.coveragePercent}%, #e4e4e7 100%)`
                      }}
                      data-testid="coverage-slider"
                    />
                    <div className="flex justify-between text-[10px] sm:text-xs text-zinc-500 mt-2">
                      <span>10%</span>
                      <span className="text-[#14532D] font-bold">100% = Factura Cero</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* System Type Selection */}
              <div className="mb-6 sm:mb-8">
                <label className="block text-xs sm:text-sm font-bold text-zinc-900 mb-3 sm:mb-4">
                  ¿Qué tipo de sistema necesitás?
                </label>
                <div className="grid grid-cols-1 gap-3">
                  {/* On-Grid (Inyección a Red) - Default */}
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, systemType: 'ongrid' })}
                    className={`p-4 rounded-xl border-2 text-left transition-all relative overflow-hidden ${
                      formData.systemType === 'ongrid'
                        ? 'border-[#90EE90] bg-[#90EE90]/10'
                        : 'border-zinc-200 hover:border-zinc-300'
                    }`}
                    data-testid="ongrid-option"
                  >
                    {/* Badge "Recomendado" */}
                    <div className="absolute top-2 right-2 bg-blue-500 text-white text-[8px] sm:text-[9px] font-bold px-2 py-0.5 rounded-full">
                      Recomendado
                    </div>
                    <div className="flex items-start gap-3">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                        formData.systemType === 'ongrid' ? 'border-[#14532D] bg-[#14532D]' : 'border-zinc-300'
                      }`}>
                        {formData.systemType === 'ongrid' && (
                          <Check className="w-3 h-3 text-white" />
                        )}
                      </div>
                      <div className="flex-1 pr-16 sm:pr-20">
                        <div className="flex items-center gap-2 mb-1">
                          <ArrowLeftRight className="w-4 h-4 text-blue-500" />
                          <p className="font-bold text-zinc-900 text-sm sm:text-base">On-Grid (Inyección a Red)</p>
                        </div>
                        <p className="text-[10px] sm:text-xs text-zinc-500">
                          <span className="font-semibold text-[#14532D]">Factura CERO.</span> Sin baterías. Vendé excedentes a ANDE durante el día y usá los créditos de noche. Ley 7599/25.
                        </p>
                      </div>
                    </div>
                  </button>
                  
                  {/* Autoconsumo */}
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, systemType: 'autoconsumo' })}
                    className={`p-4 rounded-xl border-2 text-left transition-all ${
                      formData.systemType === 'autoconsumo'
                        ? 'border-[#90EE90] bg-[#90EE90]/10'
                        : 'border-zinc-200 hover:border-zinc-300'
                    }`}
                    data-testid="autoconsumo-option"
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                        formData.systemType === 'autoconsumo' ? 'border-[#14532D] bg-[#14532D]' : 'border-zinc-300'
                      }`}>
                        {formData.systemType === 'autoconsumo' && (
                          <Check className="w-3 h-3 text-white" />
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Sun className="w-4 h-4 text-yellow-500" />
                          <p className="font-bold text-zinc-900 text-sm sm:text-base">Autoconsumo</p>
                        </div>
                        <p className="text-[10px] sm:text-xs text-zinc-500">
                          Sin baterías. Reducí tu factura usando energía solar durante el día.
                        </p>
                      </div>
                    </div>
                  </button>
                  
                  {/* Independencia Total */}
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, systemType: 'independiente' })}
                    className={`p-4 rounded-xl border-2 text-left transition-all ${
                      formData.systemType === 'independiente'
                        ? 'border-[#90EE90] bg-[#90EE90]/10'
                        : 'border-zinc-200 hover:border-zinc-300'
                    }`}
                    data-testid="independiente-option"
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                        formData.systemType === 'independiente' ? 'border-[#14532D] bg-[#14532D]' : 'border-zinc-300'
                      }`}>
                        {formData.systemType === 'independiente' && (
                          <Check className="w-3 h-3 text-white" />
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Battery className="w-4 h-4 text-green-600" />
                          <p className="font-bold text-zinc-900 text-sm sm:text-base">Independencia Total</p>
                        </div>
                        <p className="text-[10px] sm:text-xs text-zinc-500">
                          Con baterías. 100% independiente de ANDE. Ideal para zonas rurales o cortes frecuentes.
                        </p>
                      </div>
                    </div>
                  </button>
                </div>
              </div>

              {/* Name and WhatsApp - Stacked on Mobile */}
              <div className="space-y-4 sm:space-y-5 mb-6 sm:mb-8">
                <div>
                  <label className="block text-xs sm:text-sm font-bold text-zinc-900 mb-2 sm:mb-3">
                    Nombre Completo
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-zinc-400 w-4 h-4 sm:w-5 sm:h-5 sm:left-4" />
                    <input
                      type="text"
                      placeholder="Juan Pérez"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full pl-10 pr-4 py-3 sm:pl-12 sm:py-4 border-2 border-zinc-200 rounded-xl focus:border-[#90EE90] focus:outline-none text-zinc-900"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs sm:text-sm font-bold text-zinc-900 mb-2 sm:mb-3">
                    Número de WhatsApp
                  </label>
                  <PhoneInput
                    international
                    defaultCountry="PY"
                    value={formData.whatsapp}
                    onChange={(value) => setFormData({ ...formData, whatsapp: value || '+595' })}
                    className="phone-input-mobile"
                    placeholder="Ingresá tu número"
                  />
                </div>
              </div>

              {/* Submit Button */}
              <button
                onClick={handleCalculate}
                disabled={!isFormValid() || loading}
                className={`w-full py-4 sm:py-5 rounded-xl font-bold text-base sm:text-lg transition-all flex items-center justify-center gap-2 sm:gap-3 ${
                  isFormValid() && !loading
                    ? 'bg-gradient-to-r from-[#90EE90] to-[#7CDF7C] text-zinc-900 hover:shadow-xl active:scale-[0.98]'
                    : 'bg-zinc-200 text-zinc-400 cursor-not-allowed'
                }`}
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 sm:h-6 sm:w-6 border-2 border-zinc-900 border-t-transparent"></div>
                    <span className="text-sm sm:text-base">Calculando...</span>
                  </>
                ) : (
                  <>
                    Ver Mi Sistema
                    <ArrowRight className="w-5 h-5 sm:w-6 sm:h-6" />
                  </>
                )}
              </button>

              {!isFormValid() && (
                <p className="text-center text-[10px] sm:text-xs text-zinc-500 mt-3 sm:mt-4">
                  Completá todos los campos para continuar
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
      
      <Footer />
      <FloatingButtons />
    </div>
  );
};

export default CalculadoraPage;
